import { useEffect, useMemo, useState } from 'react'

interface Params<T> {
  rows: T[]
  initialPerPage?: number
}

export const useGridPagination = <T,>({ rows, initialPerPage = 12 }: Params<T>) => {
  const [page, setPage] = useState(1)
  const [perPage, setPerPageState] = useState(initialPerPage)

  const total = rows.length
  const pageCount = Math.max(1, Math.ceil(total / perPage))

  // Clamp the active page when the data set shrinks (filtering, deletion).
  useEffect(() => {
    if (page > pageCount) setPage(pageCount)
  }, [page, pageCount])

  const pageRows = useMemo(
    () => rows.slice((page - 1) * perPage, page * perPage),
    [rows, page, perPage],
  )

  const rangeStart = total === 0 ? 0 : (page - 1) * perPage + 1
  const rangeEnd = Math.min(page * perPage, total)

  const setPerPage = (n: number) => {
    setPerPageState(n)
    setPage(1)
  }

  return {
    page,
    perPage,
    pageCount,
    pageRows,
    rangeStart,
    rangeEnd,
    total,
    setPage,
    setPerPage,
  }
}

export default useGridPagination
